import { groupBy } from "lodash-es";

import { useViewerHasPermission } from "@/auth/shared";
import type { MatchRef, PlayerProfile } from "@/db/schema";
import type { MatchStatus } from "@/db/schema/shared";

import { AbandonRefForm } from "../teams/controls/abandon-ref";
import { RemoveRefForm } from "../teams/controls/remove-ref";
import { TeamNames } from "../teams/names";
import { SetMatchRefsForm } from "./set-match-refs";

// TODO: match ref teams -> match refs
// - change this (or new) file to modify refs. if refs have same teamId, operate on them equally
// - add any arbitrary profile to ref
//
// Operations to change:
// - abandon ref      should take id or teamId
// - undo abandon ref "

export type RefsListProps = {
  poolMatchId?: number;
  playoffMatchId?: number;
  tournamentDivisionId: number;
  matchStatus: MatchStatus | "tbd";
  isDemo?: boolean;
  refs: (MatchRef & {
    profile: Pick<
      PlayerProfile,
      "id" | "preferredName" | "firstName" | "lastName"
    >;
  })[];
};

export function RefsList({
  tournamentDivisionId,
  playoffMatchId,
  poolMatchId,
  matchStatus,
  refs,
  isDemo,
}: RefsListProps) {
  const canEdit = useViewerHasPermission({
    tournaments: ["update"],
  });

  const groupedRefs = groupBy(refs, "teamId");

  return (
    <div className="flex min-w-0 flex-row flex-wrap items-center gap-1 md:gap-2">
      {refs.length ? (
        <>
          <span className="whitespace-nowrap">
            Refs:{" "}
            {Object.keys(groupedRefs)
              .map((teamId) => ({
                teamId: teamId === "null" ? null : Number.parseInt(teamId, 10),
                refs: groupedRefs[teamId],
              }))
              .map(({ teamId, refs }, i, arr) => (
                <span key={teamId}>
                  <TeamNames players={refs} compact orientation="row" />
                  {i < arr.length - 1 ? ", " : ""}
                </span>
              ))}
          </span>
          {canEdit && matchStatus !== "completed" && (
            <div className="flex flex-row items-center gap-1">
              {Object.keys(groupedRefs)
                .map((teamId) => ({
                  teamId:
                    teamId === "null" ? null : Number.parseInt(teamId, 10),
                  refs: groupedRefs[teamId],
                }))
                .map(({ teamId, refs }) => (
                  <span
                    key={teamId}
                    className="flex flex-row items-center gap-1"
                  >
                    <RemoveRefForm
                      tournamentDivisionId={tournamentDivisionId}
                      ids={refs.map(({ id }) => id)}
                      teamId={teamId}
                      poolMatchId={poolMatchId}
                      playoffMatchId={playoffMatchId}
                    />
                    {teamId && (
                      <AbandonRefForm
                        tournamentDivisionId={tournamentDivisionId}
                        teamId={teamId}
                        poolMatchId={poolMatchId}
                        playoffMatchId={playoffMatchId}
                        isDemo={isDemo}
                      />
                    )}
                  </span>
                ))}
              <SetMatchRefsForm
                tournamentDivisionId={tournamentDivisionId}
                playoffMatchId={playoffMatchId}
                poolMatchId={poolMatchId}
              />
            </div>
          )}
        </>
      ) : matchStatus === "tbd" ? null : (
        <div className="flex flex-row items-center gap-1">
          <span>Self Ref</span>
          {canEdit && !["completed", "tbd"].includes(matchStatus) && (
            <SetMatchRefsForm
              tournamentDivisionId={tournamentDivisionId}
              playoffMatchId={playoffMatchId}
              poolMatchId={poolMatchId}
            />
          )}
        </div>
      )}
    </div>
  );
}
